import { Grid2, Typography } from "@mui/material";
import { useMemo } from "react";
import Breadcrumbs from "../../../../components/Breadcrumbs";
import { useTranslate } from "../../../../hooks/useTranslate";
import { useStudents } from "../../../../context/students";
import { SigneesStatus } from "../../../applicant/EnrolmentAgreement/components/Signees";
import { useGetStudentContacts } from "../../../../hooks/useGetStudentContacts";
import { useGetContract } from "../../../../hooks/useGetContract";
import Preloader from "../../../../components/Preloader";
import { useGetContractEmbedded } from "../../../../hooks/useGetContractEmbedded";
import { useAuth } from "../../../../auth";
import EmptyContent from "../../../../components/EmptyContent";
import { hasPermission, PERMISSIONS } from "../../../../constants/permissions";
import LockedPage from "../../../../components/LockedPage";
import "./Contracts.css";

export const Contracts = () => {
    const { t } = useTranslate();

    const { userId } = useAuth();
    const { activeStudentId, permissions } = useStudents();

    const canContractsView = hasPermission(permissions, PERMISSIONS.CONTRACTS_MANAGE);

    const { isLoading: isLoadingContact, contacts } = useGetStudentContacts(activeStudentId);
    const {
        isLoading, isRefetching, contract, refetch,
    } = useGetContract(activeStudentId);

    const contact = useMemo(() => contacts.find((contact) => contact.contactId === userId) || {}, [contacts]);
    const isPrimaryContact = contact?.isPrimary;
    const showSignViaEmail = !!contract && !isPrimaryContact;
    const showDocAgreement = !!contract && isPrimaryContact;
    const hideEmbedded = ["Declined", "Expired"].includes(contract?.status);

    const embeddedBody = isPrimaryContact ? {
        emailAddress: contact.emailAddress,
        name: contact.displayName,
        contactId: userId,
    } : null;

    const {
        isLoading: isLoadingEmbedded, contractEmbedded, isContractCompleted,
    } = useGetContractEmbedded(!hideEmbedded && isPrimaryContact ? contract?.contractId : null, activeStudentId, embeddedBody);

    return (
        <div className="page-content">
            <div className="student-head-section" />
            <Grid2 container spacing={2} className="min-head-section-height">
                <Grid2 size={{ xs: 12, md: 7, lg: 8 }} className="text-white">
                    <Breadcrumbs
                        breadcrumbs={[
                            { label: t("navigation.home"), href: `/home/${activeStudentId}` },
                            { label: t("finance.title"), href: `/finance/${activeStudentId}` },
                            { label: t("manageContracts.title"), current: true },
                        ]}
                    />
                    <Typography variant="h1" className="text-white">
                        {t("manageContracts.title")}
                    </Typography>
                    <Typography variant="body1" className="mt-4 text-white" component="div">
                        {t("manageContracts.subtitle")}
                    </Typography>
                </Grid2>
            </Grid2>
            <div className="z-1 relative">
                <LockedPage showPermissionText isLocked={!canContractsView}>
                    <Preloader isLoading={isLoading || isRefetching || isLoadingContact || isLoadingEmbedded}>
                        <EmptyContent data={showSignViaEmail ? [] : {}} text={t("enrolmentAgreement.signVieEmail")}>
                            {(!hideEmbedded && showDocAgreement && !isContractCompleted) && (
                                <>
                                    <Typography variant="h2" className="text-primary">
                                        {t(contract?.contractType === "Rolling" ? "finance.rollingContract" : "finance.annualContract")}
                                    </Typography>
                                    <div className="mt-8">
                                        <EmptyContent
                                            data={contractEmbedded?.url}
                                            text={(
                                                <Typography variant="body1" component="div" className="contact-help-box text-white">
                                                    {t("enrolmentAgreement.errorContract1")}
                                                    <a href="mailto:contact@kingsinterhigh.co.uk">contact@kingsinterhigh.co.uk</a>
                                                    {t("enrolmentAgreement.errorContract2")}
                                                </Typography>
                                            )}
                                        >
                                            <iframe
                                                src={contractEmbedded?.url}
                                                title="annual contract"
                                                className="EnrolmentAgreement__iframe"
                                            />
                                        </EmptyContent>
                                    </div>
                                </>
                            )}
                            {(!hideEmbedded && showDocAgreement && isContractCompleted) && (
                                <>
                                    <Typography variant="h2" className="text-primary">
                                        {t(contract?.contractType === "Rolling" ? "finance.rollingContract" : "finance.annualContract")}
                                    </Typography>
                                    <div className="contract-status">
                                        <span className="contract-status__icon">
                                            <svg viewBox="0 0 16 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path
                                                    fillRule="evenodd"
                                                    clipRule="evenodd"
                                                    d="M15.4695 0.232963C15.8241 0.561287 15.8454 1.1149 15.5171 1.46949L6.14206 11.5945C5.97228 11.7778 5.73221 11.8799 5.48237 11.8748C5.23253 11.8698 4.99677 11.7582 4.83452 11.5681L0.459523 6.44311C0.145767 6.07557 0.18937 5.52327 0.556912 5.20951C0.924454 4.89575 1.47676 4.93936 1.79051 5.3069L5.52658 9.68343L14.233 0.280522C14.5613 -0.0740672 15.1149 -0.0953599 15.4695 0.232963Z"
                                                    fill="#6CB237"
                                                />
                                            </svg>
                                        </span>
                                        <Typography variant="body1" component="div" className="contract-status__subtext">
                                            {t("enrolmentAgreement.successIssueAgreement")}
                                        </Typography>
                                    </div>
                                </>
                            )}
                            {(hideEmbedded || !showDocAgreement) && (
                                <Grid2 container>
                                    <Grid2 size={{ xs: 12, md: 8, lg: 6 }}>
                                        <Typography variant="body1" component="div" className="contact-help-box text-white">
                                            {t("enrolmentAgreement.errorContract1")}
                                            <a href="mailto:contact@kingsinterhigh.co.uk">contact@kingsinterhigh.co.uk</a>
                                            {t("enrolmentAgreement.errorContract2")}
                                        </Typography>
                                    </Grid2>
                                </Grid2>
                            )}
                        </EmptyContent>
                    </Preloader>
                </LockedPage>
            </div>
        </div>
    );
};
